import React, { ReactNode } from 'react';
import { XIcon } from '@heroicons/react/solid';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/outline';
import './Button.module.css';

export interface IButton {
	type?: 'primary' | 'secondary' | 'tertiary' | 'quaternary';
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	height?: number | null;
	width?: number | null;
	onClick?: () => void;
	isDisabled?: boolean;
	isSubmit?: boolean;
	navigate?: 'default' | 'next' | 'previous';
	xIcon?: boolean;
	children?: ReactNode;
	className?: string;
}

const Button: React.FC<IButton> = ({
	type = 'primary',
	size = 'md',
	height,
	width,
	navigate = 'default',
	xIcon = false,
	isDisabled = false,
	isSubmit = false,
	className = '',
	onClick,
	children,
}) => {
	const heightStyle = height ? { height } : {};
	const widthStyle = width ? { width } : {};

	const btnSizes = {
		xs: 'px-2 py-1 text-xs',
		sm: 'px-6 py-2 text-sm',
		md: 'px-3 py-2 text-sm',
		lg: 'px-4 py-3 text-sm',
		xl: 'px-8 py-2 text-xl',
	};

	const iconSize = {
		xs: 'h-4 mr-1',
		sm: 'h-5 mr-2',
		md: 'h-5 mr-2',
		lg: 'h-6 mr-3',
		xl: 'h-7 mr-3',
	};

	const buttonDefault = {
		primary:
			'flex justify-center items-center relative p-4 rounded-[5px] bg-primary text-primary-800 border border-primary',
		secondary:
			'flex justify-center items-center relative p-4 rounded-[5px] bg-white text-primary border border-primary',
		tertiary:
			'flex justify-center items-center relative bg-transparent text-primary border-b px-0 border-primary',
		quaternary:
			'flex justify-center items-center relative p-4 rounded-[5px] bg-transparent text-primary border border-primary',
	};

	const buttonHover = {
		primary: 'hover:bg-primary-800 hover:border-primary-800 hover:text-primary',
		secondary: 'hover:border-primary-800 hover:text-primary-800',
		tertiary: 'hover:border-transparent hover:text-primary-800',
		quaternary:
			'hover:bg-primary-800 hover:border-primary-800 hover:text-primary',
	};

	const buttonActive = {
		primary: 'active:bg-[#ffd898] active:border-[#ffd898] active:text-primary',
		secondary: 'active:border-[#ffd898] active:text-[#ffd898]',
		tertiary:
			'active:border-transparent active:text-primary-600 active:border-transparent',
		quaternary:
			'active:border-transparent active:text-primary-600 active:border-transparent',
	};

	const buttonDisabled = {
		primary:
			'disabled:bg-white disabled:border disabled:border-secondary-50 disabled:text-secondary-50',
		secondary: 'disabled:border-secondary-50 disabled:text-secondary-50',
		tertiary: 'disabled:border-transparent disabled:text-secondary-50',
		quaternary: 'disabled:border-transparent disabled:text-secondary-50',
	};

	return (
		<button
			type={isSubmit ? 'submit' : 'button'}
			className={`
			${btnSizes[size]}
			${buttonDefault[type]}
			${buttonHover[type]}
			${buttonActive[type]}
			${buttonDisabled[type]}
			${className}
			`}
			onClick={onClick}
			disabled={isDisabled}
			style={{ ...heightStyle, ...widthStyle }}
		>
			{navigate === 'previous' && (
				<ChevronLeftIcon className={'h-5 w-5 mr-1'} />
			)}
			{xIcon && <XIcon className={iconSize[size]} />}
			{children}
			{navigate === 'next' && <ChevronRightIcon className={'h-5 w-5 ml-1'} />}
		</button>
	);
};

export default Button;
